import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import axios from 'axios';
import { Download, RotateCcw, ChevronRight, Move, Maximize2, SplitSquareHorizontal, Eye, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATALOG_TABS = [
  { key: 'gresie', label: 'Gresie', category: 'placi-ceramice' },
  { key: 'wc', label: 'WC', category: 'wc', subcategory: 'wc' },
  { key: 'mobilier', label: 'Mobilier', category: 'mobilier-cu-lavoar', subcategory: 'mobilier-cu-lavoar' },
  { key: 'baterii', label: 'Baterii', category: 'baterii', subcategory: 'baterii' },
  { key: 'oglinzi', label: 'Oglinzi', category: 'oglinzi', subcategory: 'oglinzi' },
  { key: 'paravane', label: 'Paravane', category: 'pereti-dus', subcategory: 'pereti-dus' },
  { key: 'placi-cmd', label: 'Plăci Cmd.', category: 'placi-comanda', subcategory: 'placi-comanda' },
];

const SURFACE_KEYS = ['floor', 'back', 'left', 'right', 'front'];
const SURFACE_LABELS = { floor: 'Podea', back: 'Perete Spate', left: 'Perete Stâng', right: 'Perete Drept', front: 'Perete Față' };

const GROUT_COLORS = [
  { name: 'Gri', value: '#808080' },
  { name: 'Alb', value: '#f0f0f0' },
  { name: 'Negru', value: '#2a2a2a' },
  { name: 'Bej', value: '#c8b89a' },
];

const TILE_PATTERNS = [
  { name: 'Drept', value: 'straight' },
  { name: 'Decalat ½', value: 'half-offset' },
  { name: 'Decalat ⅓', value: 'third-offset' },
];

const START_POINTS = [
  { name: 'Stânga-Sus', value: 'top-left' },
  { name: 'Centru', value: 'center' },
  { name: 'Dreapta-Jos', value: 'bottom-right' },
];

// Get tile dimensions from product technical_specs
function getTileDims(product, orientation = 'horizontal') {
  const specs = product.technical_specs || [];
  let w = 0.6, h = 0.6;
  for (const s of specs) {
    const name = (s.name || s.label || '').toString();
    const val = (s.value || '').toString();
    const valMatch = val.match(/(\d{2,3})\s*[x×X]\s*(\d{2,3})/i);
    if (valMatch) { w = parseInt(valMatch[1]) / 100; h = parseInt(valMatch[2]) / 100; break; }
    const nNum = parseInt(name), vNum = parseInt(val);
    if (nNum >= 10 && nNum <= 200 && vNum >= 10 && vNum <= 200) { w = nNum / 100; h = vNum / 100; break; }
  }
  if (w === 0.6 && h === 0.6) {
    const nameMatch = (product.name || '').match(/(\d{2,3})\s*[x×X]\s*(\d{2,3})/i);
    if (nameMatch) { w = parseInt(nameMatch[1]) / 100; h = parseInt(nameMatch[2]) / 100; }
  }
  // Swap for vertical orientation (rotate tile 90°)
  if (orientation === 'vertical' && w !== h) return { w: h, h: w };
  return { w, h };
}

// Extract finish type: 'mat', 'lucios', or 'satinat' from product specs/name
function getTileFinish(product) {
  const text = [
    product.name || '',
    ...(product.technical_specs || []).map(s => `${s.name || ''} ${s.value || ''}`)
  ].join(' ').toLowerCase();
  if (text.includes('lucios') || text.includes('glossy') || text.includes('lux') || text.includes('polished')) return 'lucios';
  if (text.includes('satinat') || text.includes('satin') || text.includes('lappato')) return 'satinat';
  return 'mat'; // default
}

// Get Three.js roughness based on finish
function getFinishRoughness(product) {
  const finish = getTileFinish(product);
  if (finish === 'lucios') return 0.05;
  if (finish === 'satinat') return 0.20;
  return 0.45; // mat
}

function buildBathroom(scene, roomW, roomD, roomH) {
  const whiteMat = new THREE.MeshStandardMaterial({ color: 0xf5f5f5, roughness: 0.2 });
  const chromeMat = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.9, roughness: 0.1 });
  const cabinetMat = new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.3 });
  const glassMat = new THREE.MeshStandardMaterial({ color: 0xc8e8f0, transparent: true, opacity: 0.2, roughness: 0.05 });
  const mirrorMat = new THREE.MeshStandardMaterial({ color: 0xa8c8d8, metalness: 0.9, roughness: 0.05 });
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xe8e4df, roughness: 0.8, side: THREE.DoubleSide });
  const floorMat = new THREE.MeshStandardMaterial({ color: 0xd4cfc7, roughness: 0.6, side: THREE.DoubleSide });

  const wallThick = 0.12;

  // Floor
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(roomW, roomD), floorMat);
  floor.rotation.x = -Math.PI / 2; floor.name = 'floor'; floor.userData.surface = 'floor';
  scene.add(floor);

  // Walls (all 4) — 12cm thick BoxGeometry for realistic look
  const backWall = new THREE.Mesh(new THREE.BoxGeometry(roomW, roomH, wallThick), wallMat.clone());
  backWall.position.set(0, roomH / 2, -roomD / 2); backWall.name = 'back'; backWall.userData.surface = 'back';
  scene.add(backWall);

  const leftWall = new THREE.Mesh(new THREE.BoxGeometry(roomD, roomH, wallThick), wallMat.clone());
  leftWall.position.set(-roomW / 2, roomH / 2, 0); leftWall.rotation.y = Math.PI / 2; leftWall.name = 'left'; leftWall.userData.surface = 'left';
  scene.add(leftWall);

  const rightWall = new THREE.Mesh(new THREE.BoxGeometry(roomD, roomH, wallThick), wallMat.clone());
  rightWall.position.set(roomW / 2, roomH / 2, 0); rightWall.rotation.y = -Math.PI / 2; rightWall.name = 'right'; rightWall.userData.surface = 'right';
  scene.add(rightWall);

  const frontWall = new THREE.Mesh(new THREE.BoxGeometry(roomW, roomH, wallThick), wallMat.clone());
  frontWall.position.set(0, roomH / 2, roomD / 2); frontWall.rotation.y = Math.PI; frontWall.name = 'front'; frontWall.userData.surface = 'front';
  frontWall.visible = true;
  scene.add(frontWall);

  // Ceiling
  // No ceiling - so user can always see inside the room from above

  // === DOOR ===
  const doorGroup = new THREE.Group(); doorGroup.name = 'doorGroup'; doorGroup.userData.draggable = true; doorGroup.userData.objectType = 'door';
  doorGroup.add(new THREE.Mesh(new THREE.BoxGeometry(0.08, 2.1, 0.12), new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.3 })));
  doorGroup.children[0].position.set(0, 1.05, 0);
  const dp = new THREE.Mesh(new THREE.BoxGeometry(0.04, 2.0, 0.8), new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.4 }));
  dp.position.set(0.02, 1.0, 0.4); doorGroup.add(dp);
  const dh = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.12), chromeMat.clone());
  dh.position.set(0.05, 1.0, 0.12); doorGroup.add(dh);
  doorGroup.position.set(-roomW / 2 + 0.04, 0, 0.5);
  scene.add(doorGroup);

  // === WC SUSPENDAT (GLB MODEL) ===
  const toiletGroup = new THREE.Group(); toiletGroup.name = 'toiletGroup'; toiletGroup.userData.draggable = true; toiletGroup.userData.objectType = 'wc';
  // Placeholder until GLB loads
  const wcPlaceholder = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.15, 0.50), new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.2 }));
  wcPlaceholder.position.set(0, 0.35, 0.25); wcPlaceholder.name = 'wcPlaceholder';
  toiletGroup.add(wcPlaceholder);
  toiletGroup.position.set(-roomW * 0.32, 0, -roomD / 2 + 0.02);
  scene.add(toiletGroup);
  // Load GLB model
  const gltfLoader = new GLTFLoader();
  gltfLoader.load('https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/p5vqtfdz_element_wall_hung_wc.glb', (gltf) => {
    const model = gltf.scene;
    // Remove placeholder
    const ph = toiletGroup.getObjectByName('wcPlaceholder');
    if (ph) toiletGroup.remove(ph);
    // Auto-fit: find bounding box and scale/position to reasonable size
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const targetSize = 0.55; // target ~55cm for the largest dimension
    const scale = targetSize / maxDim;
    model.scale.setScalar(scale);
    // Re-calculate bounds after scaling
    const box2 = new THREE.Box3().setFromObject(model);
    const center = box2.getCenter(new THREE.Vector3());
    const min = box2.min;
    // Position: bottom of model at y=0.28 (suspended), back against z=0
    model.position.x -= center.x;
    model.position.y -= min.y - 0.28;
    model.position.z -= min.z;
    // Tag all meshes as wcPart for color changing
    model.traverse(c => { if (c.isMesh) { c.userData.wcPart = true; c.castShadow = true; } });
    toiletGroup.add(model);
  }, undefined, (err) => { console.warn('WC GLB load error:', err); });

  // === MOBILIER + OGLINDA (GLB MODEL) ===
  const sinkGroup = new THREE.Group(); sinkGroup.name = 'sinkGroup'; sinkGroup.userData.draggable = true; sinkGroup.userData.objectType = 'mobilier';
  const sinkPlaceholder = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 0.45), new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.15 }));
  sinkPlaceholder.position.set(0, 0.55, 0); sinkPlaceholder.name = 'sinkPlaceholder';
  sinkGroup.add(sinkPlaceholder);
  sinkGroup.position.set(roomW * 0.05, 0, -roomD / 2 + 0.15);
  scene.add(sinkGroup);

  // Mirror group (will be populated by GLB or fallback)
  const mirrorGroup = new THREE.Group(); mirrorGroup.name = 'mirrorGroup'; mirrorGroup.userData.draggable = true; mirrorGroup.userData.objectType = 'oglinda';
  mirrorGroup.position.set(-0.2, 1.5, -roomD / 2 + 0.02);
  scene.add(mirrorGroup);

  // Load GLB for mobilier + oglinda + baterie
  gltfLoader.load('https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/xog3v4hb_bathroom_asset_part_1.glb', (gltf) => {
    const model = gltf.scene;
    // Remove placeholder
    const ph = sinkGroup.getObjectByName('sinkPlaceholder');
    if (ph) sinkGroup.remove(ph);
    // Auto-fit
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const targetSize = 1.2; // target ~120cm for full unit (mobilier + oglinda)
    const scale = targetSize / maxDim;
    model.scale.setScalar(scale);
    // Re-calculate after scale
    const box2 = new THREE.Box3().setFromObject(model);
    const center = box2.getCenter(new THREE.Vector3());
    const min2 = box2.min;
    // Position: bottom on floor (y=0), back against wall (z=0)
    model.position.x -= center.x;
    model.position.y -= min2.y;
    model.position.z -= min2.z;
    model.traverse(c => { if (c.isMesh) { c.castShadow = true; c.receiveShadow = true;
      // Hide flat floor-level meshes (base/platform that looks like a carpet)
      const meshBox = new THREE.Box3().setFromObject(c);
      const meshSize = meshBox.getSize(new THREE.Vector3());
      if (meshSize.y < 0.02 && meshSize.x > 0.3 && meshSize.z > 0.3) {
        c.visible = false; // Hide flat base/platform
      }
    } });
    sinkGroup.add(model);
    // Hide the separate mirror group since GLB includes mirror
    mirrorGroup.visible = false;
  }, undefined, (err) => {
    console.warn('Mobilier GLB load error:', err);
    // Fallback: show basic mirror
    mirrorGroup.add(new THREE.Mesh(new THREE.RingGeometry(0.35, 0.38, 32), new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.4 })));
    const mG = new THREE.Mesh(new THREE.CircleGeometry(0.35, 32), mirrorMat.clone()); mG.position.z = 0.01; mirrorGroup.add(mG);
  });

  // === SHOWER WALK-IN (RADAWAY MODO NEW III GLB) ===
  const showerGroup = new THREE.Group(); showerGroup.name = 'showerGroup'; showerGroup.userData.draggable = true; showerGroup.userData.objectType = 'paravan';
  showerGroup.position.set(roomW / 2 - 0.52, 0, -roomD / 2 + 0.52);
  scene.add(showerGroup);
  // Load Radaway GLB
  gltfLoader.load('/models/shower_100x100.glb', (gltf) => {
    const model = gltf.scene;
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 2.1 / maxDim; // target ~210cm height
    model.scale.setScalar(scale);
    const box2 = new THREE.Box3().setFromObject(model);
    const min2 = box2.min;
    const center = box2.getCenter(new THREE.Vector3());
    model.position.x -= center.x;
    model.position.y -= min2.y;
    model.position.z -= center.z;
    // Apply proper materials: glass (transparent) + chrome (metallic)
    model.traverse(c => {
      if (c.isMesh) {
        c.castShadow = true;
        const geoBBox = new THREE.Box3().setFromObject(c);
        const geoSize = geoBBox.getSize(new THREE.Vector3());
        const isLargeFlat = (geoSize.x > 0.3 || geoSize.z > 0.3) && geoSize.y > 0.5;
        if (isLargeFlat) {
          // Large flat panels = glass
          c.material = new THREE.MeshStandardMaterial({ color: 0xeef4ff, transparent: true, opacity: 0.18, roughness: 0.02, metalness: 0.05, side: THREE.DoubleSide });
        } else {
          // Profiles, hinges, handles = chrome
          c.material = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.9, roughness: 0.1 });
        }
      }
    });
    showerGroup.add(model);
  }, undefined, (err) => {
    console.warn('Shower GLB error:', err);
    // Fallback: basic glass + chrome
    const shGlass = new THREE.Mesh(new THREE.BoxGeometry(0.015, 2.15, 1.0), glassMat.clone());
    shGlass.position.set(-0.5, 1.08, 0); showerGroup.add(shGlass);
    const shProf = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.025, 1.02), chromeMat.clone());
    shProf.position.set(-0.5, 2.16, 0); showerGroup.add(shProf);
    const colRail = new THREE.Mesh(new THREE.BoxGeometry(0.03, 1.3, 0.03), chromeMat.clone());
    colRail.position.set(0.48, 1.35, 0); showerGroup.add(colRail);
    const rainHead = new THREE.Mesh(new THREE.BoxGeometry(0.25, 0.015, 0.25), chromeMat.clone());
    rainHead.position.set(0.08, 2.15, 0); showerGroup.add(rainHead);
  });

  // Position shower in right-back corner
  showerGroup.position.set(roomW / 2 - 0.52, 0, -roomD / 2 + 0.52);
  scene.add(showerGroup);

  return { floor, backWall, leftWall, rightWall, frontWall, toiletGroup, sinkGroup, mirrorGroup, showerGroup, doorGroup };
}

// ===== WALL ZONE SPLIT COMPONENT (CM-BASED) =====
const WallZonePanel = ({ surfaceKey, zones, setZones, products, getImageUrl, roomW, roomD, roomH, selectedZoneIdx, setSelectedZoneIdx }) => {
  const currentZones = zones[surfaceKey] || [{ pct: 100, product: null }];
  const isFloor = surfaceKey === 'floor';
  // Surface width in cm
  const surfWidthCm = Math.round((isFloor ? roomW : (surfaceKey === 'left' || surfaceKey === 'right' ? roomD : roomW)) * 100);

  const getZoneCm = (z) => Math.round(surfWidthCm * z.pct / 100);

  const addZone = () => {
    if (currentZones.length >= 4) return;
    const newZones = currentZones.map(z => ({ ...z, pct: Math.floor(z.pct / 2) }));
    newZones.push({ pct: Math.floor(100 / (currentZones.length + 1)), product: null });
    const total = newZones.reduce((s, z) => s + z.pct, 0);
    if (total !== 100) newZones[newZones.length - 1].pct += (100 - total);
    setZones(prev => ({ ...prev, [surfaceKey]: newZones }));
  };

  const removeZone = (idx) => {
    if (currentZones.length <= 1) return;
    const updated = currentZones.filter((_, i) => i !== idx);
    const each = Math.floor(100 / updated.length);
    const normalized = updated.map((z, i) => ({ ...z, pct: i === updated.length - 1 ? 100 - each * (updated.length - 1) : each }));
    setZones(prev => ({ ...prev, [surfaceKey]: normalized }));
  };

  const updateCm = (idx, cmVal) => {
    const cm = Math.max(10, Math.min(surfWidthCm - 10 * (currentZones.length - 1), parseInt(cmVal) || 10));
    const pct = Math.round(cm / surfWidthCm * 100);
    const updated = [...currentZones];
    updated[idx] = { ...updated[idx], pct: Math.max(3, Math.min(97, pct)) };
    // Adjust last zone to fill remaining
    const others = updated.reduce((s, z, i) => i !== updated.length - 1 ? s + z.pct : s, 0);
    updated[updated.length - 1] = { ...updated[updated.length - 1], pct: Math.max(3, 100 - others) };
    setZones(prev => ({ ...prev, [surfaceKey]: updated }));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[#D4AF37] text-xs font-semibold">{SURFACE_LABELS[surfaceKey]} <span className="text-[#666] font-normal">({surfWidthCm} cm total)</span></span>
        <button onClick={addZone} className="text-xs text-[#A3A3A3] hover:text-[#D4AF37] border border-white/10 px-2 py-0.5 rounded" disabled={currentZones.length >= 4}>
          + Adaugă Zonă
        </button>
      </div>
      {/* Visual preview bar */}
      <div className="flex h-8 rounded overflow-hidden border border-white/10">
        {currentZones.map((z, i) => {
          const cm = getZoneCm(z);
          return (
            <div key={i} style={{ width: `${z.pct}%` }} className={`flex items-center justify-center text-[10px] font-bold ${z.product ? 'bg-[#D4AF37]/30 text-[#D4AF37]' : 'bg-[#222] text-[#666]'} ${i > 0 ? 'border-l border-white/20' : ''}`}>
              {cm} cm {z.product ? '' : '(gol)'}
            </div>
          );
        })}
      </div>
      <div className="text-[#555] text-[9px] mt-1">Total: {surfWidthCm} cm | Înălțime: {Math.round((isFloor ? roomD : roomH) * 100)} cm</div>
      {currentZones.map((z, i) => (
        <div key={i} onClick={() => setSelectedZoneIdx(i)}
          className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${i === Math.min(selectedZoneIdx, currentZones.length - 1) ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40' : 'bg-[#1a1a1a] border-white/5 hover:border-white/15'}`}
          data-testid={`zone-select-${i}`}>
          <span className={`text-[10px] w-10 font-semibold ${i === Math.min(selectedZoneIdx, currentZones.length - 1) ? 'text-[#D4AF37]' : 'text-[#555]'}`}>
            {i === 0 ? 'Stânga' : i === currentZones.length - 1 ? 'Dreapta' : `Z${i+1}`}
          </span>
          <input type="number" value={getZoneCm(z)} onChange={e => updateCm(i, e.target.value)} min="10" max={surfWidthCm}
            className="w-14 bg-[#0a0a0a] border border-white/10 text-white text-xs px-1 py-1 rounded text-center" />
          <span className="text-[#666] text-xs">cm</span>
          <div className="flex-1 min-w-0">
            {z.product ? (
              <div className="flex items-center gap-1">
                {getImageUrl(z.product) && <img src={getImageUrl(z.product)} alt="" className="w-6 h-6 rounded object-cover" />}
                <span className="text-white text-xs truncate">{z.product.name}</span>
              </div>
            ) : (
              <span className="text-[#555] text-xs italic">Selectează din catalog</span>
            )}
          </div>
          {currentZones.length > 1 && (
            <button onClick={() => removeZone(i)} className="text-red-400/60 hover:text-red-400 text-xs">✕</button>
          )}
        </div>
      ))}
    </div>
  );
};

// ===== MAIN PAGE =====
const BathroomConfiguratorPage = () => {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objectsRef = useRef({});
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('gresie');
  const [selectedSurface, setSelectedSurface] = useState('floor');
  const [selectedZoneIdx, setSelectedZoneIdx] = useState(0);
  const [dragMode, setDragMode] = useState(false);
  const [zonesHistory, setZonesHistory] = useState([]);
  const [roomWidth, setRoomWidth] = useState(3.4);
  const [roomDepth, setRoomDepth] = useState(3.2);
  const [roomHeight, setRoomHeight] = useState(2.7);
  const [viewMode, setViewMode] = useState('perspective'); // 'perspective' or 'top'
  const [tilePattern, setTilePattern] = useState('straight');
  const [groutColor, setGroutColor] = useState('#808080');
  const [startPoint, setStartPoint] = useState('top-left');
  const [wcColor, setWcColor] = useState('#ffffff');
  const [mirrorShape, setMirrorShape] = useState('round');
  const [pdfWithPrice, setPdfWithPrice] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [furnitureSize, setFurnitureSize] = useState(80); // 60, 80, 120 cm
  const [furnitureColor, setFurnitureColor] = useState('lemn'); // lemn, alb, gri, negru
  const [bathType, setBathType] = useState('shower'); // shower, bathtub
  const [tileOrientation, setTileOrientation] = useState('horizontal'); // horizontal, vertical
  // zones: { floor: [{pct:100, product:null}], back: [...], ... }
  const [zones, setZones] = useState(() => {
    const init = {};
    SURFACE_KEYS.forEach(k => { init[k] = [{ pct: 100, product: null }]; });
    return init;
  });

  // Fetch products per tab
  useEffect(() => {
    const tab = CATALOG_TABS.find(t => t.key === activeTab);
    if (!tab || products[activeTab]) return;
    setLoading(true);
    const url = tab.subcategory
      ? `${API_URL}/api/products?subcategory=${tab.subcategory}&active_only=true`
      : `${API_URL}/api/products?category=${tab.category}&active_only=true`;
    axios.get(url)
      .then(r => setProducts(prev => ({ ...prev, [activeTab]: r.data })))
      .catch(() => setProducts(prev => ({ ...prev, [activeTab]: [] })))
      .finally(() => setLoading(false));
  }, [activeTab]);

  const getImageUrl = (product) => {
    const img = product?.images?.[0];
    if (!img) return null;
    return img.startsWith('/api') ? `${API_URL}${img}` : img;
  };

  // Build 3D scene
  const buildScene = useCallback(() => {
    const container = mountRef.current;
    if (!container) return;
    if (rendererRef.current) {
      rendererRef.current.dispose();
      const old = container.querySelector('canvas');
      if (old) container.removeChild(old);
    }
    const w = container.clientWidth, h = 500;
    const scene = new THREE.Scene(); scene.background = new THREE.Color(0x2a2a2a); sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.05, 200);
    // Elevated exterior "dollhouse" view - looking down into the room from outside
    camera.position.set(roomWidth * 0.8, roomHeight * 1.6, roomDepth * 1.3);
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h); renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement); rendererRef.current = renderer;
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, roomHeight * 0.15, -roomDepth * 0.05);
    controls.minPolarAngle = 0.1; controls.maxPolarAngle = Math.PI * 0.55;
    controls.minDistance = 2.0; controls.maxDistance = Math.max(roomWidth, roomDepth) * 4;
    controls.enablePan = true; controls.update(); controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xffffff, 1.5));
    const dl = new THREE.DirectionalLight(0xffffff, 1.2); dl.position.set(3, 6, 4); dl.castShadow = true; scene.add(dl);
    const dl2 = new THREE.DirectionalLight(0xffffff, 0.8); dl2.position.set(-3, 4, -2); scene.add(dl2);
    const pl = new THREE.PointLight(0xffffff, 1.0); pl.position.set(0, roomHeight - 0.1, 0); scene.add(pl);
    const pl2 = new THREE.PointLight(0xfff5e0, 0.6); pl2.position.set(roomWidth * 0.3, roomHeight * 0.5, roomDepth * 0.3); scene.add(pl2);
    const pl3 = new THREE.PointLight(0xffffff, 0.6); pl3.position.set(-roomWidth * 0.3, roomHeight * 0.5, -roomDepth * 0.3); scene.add(pl3);

    const objs = buildBathroom(scene, roomWidth, roomDepth, roomHeight);
    objectsRef.current = objs;

    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersection = new THREE.Vector3();
    let isDragging = false, dragObj = null;

    let frameId;
    const animate = () => { frameId = requestAnimationFrame(animate); controls.update(); renderer.render(scene, camera); };
    animate();

    const onResize = () => { const nw = container.clientWidth; camera.aspect = nw / h; camera.updateProjectionMatrix(); renderer.setSize(nw, h); };
    window.addEventListener('resize', onResize);

    const getObj = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const hits = raycasterRef.current.intersectObjects(scene.children.filter(c => c.userData?.draggable), true);
      if (hits.length) { let o = hits[0].object; while (o.parent && !o.userData?.draggable) o = o.parent; if (o.userData?.draggable) return o; }
      return null;
    };

    const onDown = (e) => {
      if (!dragMode) {
        const rect = renderer.domElement.getBoundingClientRect();
        mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycasterRef.current.setFromCamera(mouseRef.current, camera);
        for (const hit of raycasterRef.current.intersectObjects(scene.children, true)) {
          if (hit.object.userData.surface) { setSelectedSurface(hit.object.userData.surface); return; }
        }
        return;
      }
      const o = getObj(e);
      if (o) { isDragging = true; dragObj = o; controls.enabled = false; renderer.domElement.style.cursor = 'grabbing'; }
    };
    const onMove = (e) => {
      if (!isDragging || !dragObj) return;
      const rect = renderer.domElement.getBoundingClientRect();
      mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      if (raycasterRef.current.ray.intersectPlane(dragPlane, intersection)) {
        const hw = roomWidth / 2 - 0.3, hd = roomDepth / 2 - 0.3;
        dragObj.position.x = Math.max(-hw, Math.min(hw, intersection.x));
        dragObj.position.z = Math.max(-hd, Math.min(hd, intersection.z));
      }
    };
    const onUp = () => { if (isDragging) { isDragging = false; dragObj = null; controls.enabled = true; renderer.domElement.style.cursor = dragMode ? 'grab' : 'default'; } };

    renderer.domElement.addEventListener('pointerdown', onDown);
    renderer.domElement.addEventListener('pointermove', onMove);
    renderer.domElement.addEventListener('pointerup', onUp);

    return () => {
      cancelAnimationFrame(frameId); window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', onDown);
      renderer.domElement.removeEventListener('pointermove', onMove);
      renderer.domElement.removeEventListener('pointerup', onUp);
      renderer.dispose(); if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [roomWidth, roomDepth, roomHeight, dragMode]);

  useEffect(() => { const cleanup = buildScene(); return cleanup; }, [buildScene]);

  // Switch camera view mode and front wall visibility
  useEffect(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const objs = objectsRef.current;
    if (!camera || !controls) return;

    if (viewMode === 'top') {
      // Top-down bird's eye view
      const viewDist = Math.max(roomWidth, roomDepth) * 1.8;
      camera.position.set(0, viewDist, 0.01);
      controls.target.set(0, 0, 0);
      if (objs.frontWall) objs.frontWall.visible = true;
    } else {
      // Perspective / lateral dollhouse view
      camera.position.set(roomWidth * 0.8, roomHeight * 1.6, roomDepth * 1.3);
      controls.target.set(0, roomHeight * 0.15, -roomDepth * 0.05);
      if (objs.frontWall) objs.frontWall.visible = false;
    }
    controls.update();
  }, [viewMode, roomWidth, roomDepth, roomHeight]);

  // Update WC color at runtime
  useEffect(() => {
    const objs = objectsRef.current;
    if (!objs.toiletGroup) return;
    const color = new THREE.Color(wcColor);
    objs.toiletGroup.traverse(c => {
      if (c.isMesh && c.userData.wcPart) {
        c.material.color.copy(color);
      }
    });
  }, [wcColor]);

  // Update mirror shape at runtime
  useEffect(() => {
    const objs = objectsRef.current;
    const scene = sceneRef.current;
    if (!objs.mirrorGroup || !scene) return;
    const mg = objs.mirrorGroup;
    // Remove old children
    while (mg.children.length > 0) mg.remove(mg.children[0]);
    const mirrorMat = new THREE.MeshStandardMaterial({ color: 0xa8c8d8, metalness: 0.9, roughness: 0.05 });
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.4 });
    if (mirrorShape === 'round') {
      mg.add(new THREE.Mesh(new THREE.RingGeometry(0.30, 0.33, 32), frameMat));
      const glass = new THREE.Mesh(new THREE.CircleGeometry(0.30, 32), mirrorMat);
      glass.position.z = 0.01; mg.add(glass);
    } else if (mirrorShape === 'rectangular') {
      mg.add(new THREE.Mesh(new THREE.BoxGeometry(0.60, 0.80, 0.02), frameMat));
      const glass = new THREE.Mesh(new THREE.PlaneGeometry(0.56, 0.76), mirrorMat);
      glass.position.z = 0.015; mg.add(glass);
    } else if (mirrorShape === 'oval') {
      const ovalShape = new THREE.Shape();
      ovalShape.ellipse(0, 0, 0.25, 0.40, 0, Math.PI * 2, false, 0);
      const frameGeo = new THREE.ExtrudeGeometry(ovalShape, { depth: 0.02, bevelEnabled: false });
      mg.add(new THREE.Mesh(frameGeo, frameMat));
      const innerShape = new THREE.Shape();
      innerShape.ellipse(0, 0, 0.22, 0.37, 0, Math.PI * 2, false, 0);
      const glassGeo = new THREE.ExtrudeGeometry(innerShape, { depth: 0.005, bevelEnabled: false });
      const glass = new THREE.Mesh(glassGeo, mirrorMat);
      glass.position.z = 0.018; mg.add(glass);
    }
  }, [mirrorShape]);

  // Update furniture SIZE (scale X axis proportionally)
  useEffect(() => {
    const objs = objectsRef.current;
    if (!objs.sinkGroup) return;
    const baseSize = 80; // base model assumed ~80cm
    const scaleFactor = furnitureSize / baseSize;
    // Scale only X for width, keep Y and Z the same
    objs.sinkGroup.traverse(c => {
      if (c.name === 'sinkGroup') return; // skip group itself
    });
    // Apply to the loaded GLB child (first child that isn't placeholder)
    objs.sinkGroup.children.forEach(child => {
      if (child.name === 'sinkPlaceholder') return;
      child.scale.x = scaleFactor;
    });
  }, [furnitureSize]);

  // Update furniture COLOR
  useEffect(() => {
    const objs = objectsRef.current;
    if (!objs.sinkGroup) return;
    const colorMap = {
      'lemn': null, // keep original GLB materials
      'alb': new THREE.Color(0xf5f5f5),
      'gri': new THREE.Color(0x888888),
      'negru': new THREE.Color(0x222222),
    };
    const targetColor = colorMap[furnitureColor];
    objs.sinkGroup.traverse(c => {
      if (!c.isMesh) return;
      if (furnitureColor === 'lemn') {
        // Restore original material if saved
        if (c.userData.originalColor) {
          c.material.color.copy(c.userData.originalColor);
        }
      } else {
        // Save original color first time
        if (!c.userData.originalColor) {
          c.userData.originalColor = c.material.color.clone();
        }
        // Only change wood/cabinet parts (skip chrome/mirror parts)
        const lum = c.userData.originalColor.r;
        if (lum > 0.15 && lum < 0.95) { // skip very dark (chrome accents) and very bright (porcelain)
          c.material.color.copy(targetColor);
        }
      }
    });
  }, [furnitureColor]);

  // Switch between shower and bathtub
  useEffect(() => {
    const objs = objectsRef.current;
    const scene = sceneRef.current;
    if (!scene) return;

    if (bathType === 'bathtub') {
      // Hide shower, show/load bathtub
      if (objs.showerGroup) objs.showerGroup.visible = false;
      if (!objs.bathtubGroup) {
        const bathtubGroup = new THREE.Group();
        bathtubGroup.name = 'bathtubGroup';
        bathtubGroup.userData.draggable = true;
        bathtubGroup.userData.objectType = 'cada';
        bathtubGroup.position.set(roomWidth / 2 - 0.52, 0, -roomDepth / 2 + 0.52);
        scene.add(bathtubGroup);
        objs.bathtubGroup = bathtubGroup;
        const gltfLoader = new GLTFLoader();
        gltfLoader.load('https://customer-assets-m6fa6gv7.emergentagent.net/job_presentation-site-11/artifacts/ery8za8u_bathtub.glb', (gltf) => {
          const model = gltf.scene;
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 1.5 / maxDim;
          model.scale.setScalar(scale);
          const box2 = new THREE.Box3().setFromObject(model);
          const min2 = box2.min;
          const center = box2.getCenter(new THREE.Vector3());
          model.position.x -= center.x;
          model.position.y -= min2.y;
          model.position.z -= center.z;
          model.traverse(c => { if (c.isMesh) { c.castShadow = true; } });
          bathtubGroup.add(model);
        }, undefined, (err) => console.warn('Bathtub GLB error:', err));
      } else {
        objs.bathtubGroup.visible = true;
      }
    } else {
      // Show shower, hide bathtub
      if (objs.showerGroup) objs.showerGroup.visible = true;
      if (objs.bathtubGroup) objs.bathtubGroup.visible = false;
    }
  }, [bathType, roomWidth, roomDepth]);

  // Apply zones to 3D (rebuild wall textures when zones change)
  useEffect(() => {
    const objs = objectsRef.current;
    if (!objs.floor) return;

    const surfMap = { floor: objs.floor, back: objs.backWall, left: objs.leftWall, right: objs.rightWall, front: objs.frontWall };
    const CANVAS_SIZE = 2048;
    const GROUT_PX = 4; // grout line width in pixels

    const getAllImageUrls = (product) => {
      return (product?.images || []).map(img => img.startsWith('/api') ? `${API_URL}${img}` : img).filter(Boolean);
    };

    // Core function: draw tiles with pattern, grout, start point
    const drawTilesOnCanvas = (ctx, validImgs, areaX, areaY, areaW, areaH, tilePxW, tilePxH) => {
      // Fill area with grout color
      ctx.fillStyle = groutColor;
      ctx.fillRect(areaX, areaY, areaW, areaH);

      // Calculate start offset
      let offX = 0, offY = 0;
      if (startPoint === 'center') {
        offX = (areaW % tilePxW) / 2;
        offY = (areaH % tilePxH) / 2;
      } else if (startPoint === 'bottom-right') {
        offX = areaW % tilePxW;
        offY = areaH % tilePxH;
      }

      const rows = Math.ceil(areaH / tilePxH) + 2;
      const cols = Math.ceil(areaW / tilePxW) + 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(areaX, areaY, areaW, areaH);
      ctx.clip();

      for (let row = -1; row < rows; row++) {
        let rowShift = 0;
        if (tilePattern === 'half-offset') rowShift = (((row % 2) + 2) % 2) * tilePxW * 0.5;
        else if (tilePattern === 'third-offset') rowShift = (((row % 3) + 3) % 3) * tilePxW / 3;

        for (let col = -1; col < cols; col++) {
          const x = areaX + offX + col * tilePxW + rowShift;
          const y = areaY + offY + row * tilePxH;

          const pick = validImgs[Math.floor(Math.random() * validImgs.length)];
          // Draw tile slightly smaller to show grout gap
          ctx.drawImage(pick, x + GROUT_PX / 2, y + GROUT_PX / 2, tilePxW - GROUT_PX, tilePxH - GROUT_PX);
        }
      }
      ctx.restore();
    };

    // Load images for a product and call back with loaded images
    const loadProductImages = (allUrls, callback) => {
      if (allUrls.length === 0) return;
      const loadedImgs = [];
      let loadCount = 0;
      allUrls.forEach((url, idx) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { loadedImgs[idx] = img; loadCount++; if (loadCount === allUrls.length) { const valid = loadedImgs.filter(Boolean); if (valid.length > 0) callback(valid); } };
        img.onerror = () => { loadCount++; if (loadCount === allUrls.length) { const valid = loadedImgs.filter(Boolean); if (valid.length > 0) callback(valid); } };
        img.src = url;
      });
    };

    for (const [surfKey, mesh] of Object.entries(surfMap)) {
      if (!mesh) continue;
      const surfZones = zones[surfKey] || [{ pct: 100, product: null }];
      const anyProduct = surfZones.some(z => z.product);
      if (!anyProduct) continue;

      const params = mesh.geometry.parameters;
      const surfW = params.width || 3;
      const surfH = params.height || 3;

      if (surfZones.length === 1 && surfZones[0].product) {
        const product = surfZones[0].product;
        const allUrls = getAllImageUrls(product);
        const dims = getTileDims(product, tileOrientation);
        if (allUrls.length === 0) continue;

        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_SIZE; canvas.height = CANVAS_SIZE;
        const ctx = canvas.getContext('2d');
        const tilesX = Math.max(1, Math.round(surfW / dims.w));
        const tilesY = Math.max(1, Math.round(surfH / dims.h));
        const tilePxW = Math.floor(CANVAS_SIZE / tilesX);
        const tilePxH = Math.floor(CANVAS_SIZE / tilesY);

        loadProductImages(allUrls, (validImgs) => {
          drawTilesOnCanvas(ctx, validImgs, 0, 0, CANVAS_SIZE, CANVAS_SIZE, tilePxW, tilePxH);
          const tex = new THREE.CanvasTexture(canvas);
          tex.colorSpace = THREE.SRGBColorSpace;
          mesh.material = new THREE.MeshStandardMaterial({ map: tex, roughness: getFinishRoughness(product), metalness: 0.02, side: THREE.FrontSide });
        });

      } else if (surfZones.length > 1) {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_SIZE; canvas.height = CANVAS_SIZE;
        const ctx = canvas.getContext('2d');
        let completedZones = 0;
        const totalToLoad = surfZones.filter(z => z.product).length;
        if (totalToLoad === 0) continue;

        let xOffset = 0;
        surfZones.forEach((z) => {
          const zonePx = Math.round(CANVAS_SIZE * z.pct / 100);
          if (!z.product) {
            ctx.fillStyle = surfKey === 'floor' ? '#d4cfc7' : '#e8e4df';
            ctx.fillRect(xOffset, 0, zonePx, CANVAS_SIZE);
            xOffset += zonePx;
            return;
          }
          const allUrls = getAllImageUrls(z.product);
          const dims = getTileDims(z.product, tileOrientation);
          const capturedX = xOffset;
          if (allUrls.length === 0) { xOffset += zonePx; return; }

          const zoneWidthM = surfW * z.pct / 100;
          const tilesX = Math.max(1, Math.round(zoneWidthM / dims.w));
          const tilesY = Math.max(1, Math.round(surfH / dims.h));
          const tilePxW = Math.floor(zonePx / tilesX);
          const tilePxH = Math.floor(CANVAS_SIZE / tilesY);

          loadProductImages(allUrls, (validImgs) => {
            drawTilesOnCanvas(ctx, validImgs, capturedX, 0, zonePx, CANVAS_SIZE, tilePxW, tilePxH);
            completedZones++;
            if (completedZones >= totalToLoad) {
              const tex = new THREE.CanvasTexture(canvas);
              tex.colorSpace = THREE.SRGBColorSpace;
              mesh.material = new THREE.MeshStandardMaterial({ map: tex, roughness: getFinishRoughness(z.product), metalness: 0.02, side: THREE.FrontSide });
            }
          });
          xOffset += zonePx;
        });
      }
    }
  }, [zones, tilePattern, groutColor, startPoint, tileOrientation]);

  const handleProductClick = (product) => {
    if (activeTab === 'gresie') {
      setZonesHistory(prev => [...prev.slice(-9), JSON.parse(JSON.stringify(zones))]);
      const surfZones = zones[selectedSurface] || [{ pct: 100, product: null }];
      const idx = Math.min(selectedZoneIdx, surfZones.length - 1);
      const updated = [...surfZones];
      updated[idx] = { ...updated[idx], product };
      setZones(prev => ({ ...prev, [selectedSurface]: updated }));
    } else {
      const typeMap = { wc: 'toiletGroup', mobilier: 'sinkGroup', oglinzi: 'mirrorGroup', paravane: 'showerGroup' };
      const groupName = typeMap[activeTab];
      const group = groupName ? objectsRef.current[groupName] : null;
      if (group) {
        const url = getImageUrl(product);
        if (url) {
          const loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
          loader.load(url, (tex) => {
            tex.colorSpace = THREE.SRGBColorSpace;
            group.traverse(c => { if (c.isMesh) c.material = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.3 }); });
          });
        }
      }
    }
  };

  const undoLast = () => {
    if (zonesHistory.length === 0) return;
    const prev = zonesHistory[zonesHistory.length - 1];
    setZonesHistory(h => h.slice(0, -1));
    setZones(prev);
  };

  const resetAll = () => {
    setZonesHistory(prev => [...prev.slice(-9), JSON.parse(JSON.stringify(zones))]);
    const init = {};
    SURFACE_KEYS.forEach(k => { init[k] = [{ pct: 100, product: null }]; });
    setZones(init);
    setSelectedZoneIdx(0);
  };

  const takeScreenshot = () => {
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      const url = rendererRef.current.domElement.toDataURL('image/png');
      const a = document.createElement('a'); a.download = 'baie-configurator-shagun.png'; a.href = url; a.click();
    }
  };

  // Capture a view as base64 image
  const captureView = (mode) => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const objs = objectsRef.current;
    if (!camera || !controls || !renderer || !scene) return null;

    const origPos = camera.position.clone();
    const origTarget = controls.target.clone();
    const origFrontVisible = objs.frontWall?.visible;
    // Save visibility of all major objects
    const wallKeys = ['floor', 'backWall', 'leftWall', 'rightWall', 'frontWall', 'toiletGroup', 'sinkGroup', 'mirrorGroup', 'showerGroup', 'doorGroup'];
    const origVis = {};
    wallKeys.forEach(k => { if (objs[k]) origVis[k] = objs[k].visible; });
    const origFov = camera.fov;

    const showOnly = (...keys) => {
      wallKeys.forEach(k => { if (objs[k]) objs[k].visible = false; });
      keys.forEach(k => { if (objs[k]) objs[k].visible = true; });
    };

    if (mode === 'top') {
      const viewDist = Math.max(roomWidth, roomDepth) * 1.8;
      camera.position.set(0, viewDist, 0.01);
      controls.target.set(0, 0, 0);
      wallKeys.forEach(k => { if (objs[k]) objs[k].visible = origVis[k] !== undefined ? origVis[k] : true; });
      if (objs.frontWall) objs.frontWall.visible = true;
    } else if (mode === 'back') {
      showOnly('backWall');
      camera.fov = 42; camera.position.set(0, roomHeight / 2, roomDepth * 0.9);
      controls.target.set(0, roomHeight / 2, -roomDepth / 2);
    } else if (mode === 'left') {
      showOnly('leftWall');
      camera.fov = 42; camera.position.set(roomWidth * 0.9, roomHeight / 2, 0);
      controls.target.set(-roomWidth / 2, roomHeight / 2, 0);
    } else if (mode === 'right') {
      showOnly('rightWall');
      camera.fov = 42; camera.position.set(-roomWidth * 0.9, roomHeight / 2, 0);
      controls.target.set(roomWidth / 2, roomHeight / 2, 0);
    } else if (mode === 'front') {
      showOnly('frontWall');
      camera.fov = 42; camera.position.set(0, roomHeight / 2, -roomDepth * 0.9);
      controls.target.set(0, roomHeight / 2, roomDepth / 2);
    } else if (mode === 'floor') {
      showOnly('floor');
      camera.fov = 45; camera.position.set(0, Math.max(roomWidth, roomDepth) * 1.4, 0.01);
      controls.target.set(0, 0, 0);
    } else {
      wallKeys.forEach(k => { if (objs[k]) objs[k].visible = origVis[k] !== undefined ? origVis[k] : true; });
      camera.position.set(roomWidth * 0.8, roomHeight * 1.6, roomDepth * 1.3);
      controls.target.set(0, roomHeight * 0.15, -roomDepth * 0.05);
      if (objs.frontWall) objs.frontWall.visible = false;
    }
    camera.updateProjectionMatrix();
    controls.update();
    renderer.render(scene, camera);
    const dataUrl = renderer.domElement.toDataURL('image/jpeg', 0.85);

    // Restore everything
    camera.fov = origFov; camera.updateProjectionMatrix();
    camera.position.copy(origPos);
    controls.target.copy(origTarget);
    wallKeys.forEach(k => { if (objs[k]) objs[k].visible = origVis[k] !== undefined ? origVis[k] : true; });
    controls.update();
    renderer.render(scene, camera);
    return dataUrl;
  };

  const getPatternLabel = () => tilePattern === 'half-offset' ? 'Decalat la jumatate' : tilePattern === 'third-offset' ? 'Decalat la treime' : 'Drept';
  const getStartLabel = () => startPoint === 'center' ? 'din centru' : startPoint === 'bottom-right' ? 'din dreapta-jos' : 'din stanga-sus';
  // jsPDF nu suporta diacritice romanesti - le inlocuim
  const fixRo = (s) => s.replace(/[ăâ]/g,'a').replace(/[ĂÂ]/g,'A').replace(/[î]/g,'i').replace(/[Î]/g,'I').replace(/[ș]/g,'s').replace(/[Ș]/g,'S').replace(/[ț]/g,'t').replace(/[Ț]/g,'T');

  const SURFACE_LABELS_PDF = { floor: 'Podea', back: 'Perete Spate', left: 'Perete Stang', right: 'Perete Drept', front: 'Perete Fata' };

  const exportPDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pw = 210;
    let y = 15;

    // PAGE 1: Overview
    pdf.setFontSize(20); pdf.setFont('helvetica', 'bold');
    pdf.text('SHAGUN SURFACES', pw / 2, y, { align: 'center' }); y += 7;
    pdf.setFontSize(14);
    pdf.text('Plan Montaj Baie', pw / 2, y, { align: 'center' }); y += 10;

    pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(80);
    pdf.text(`Camera: ${roomWidth}m x ${roomDepth}m x ${roomHeight}m`, 15, y); y += 5;
    pdf.text(`Montaj: ${getPatternLabel()} | Plecare: ${getStartLabel()}`, 15, y); y += 5;
    pdf.text(`Culoare chit: ${GROUT_COLORS.find(g => g.value === groutColor)?.name || 'Gri'} (rost 2mm)`, 15, y);
    pdf.setTextColor(0); y += 8;

    const lateralImg = captureView('perspective');
    if (lateralImg) { pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.text('Vedere Generala', 15, y); y += 3; pdf.addImage(lateralImg, 'JPEG', 15, y, 180, 75); y += 79; }
    const topImg = captureView('top');
    if (topImg) { pdf.setFont('helvetica', 'bold'); pdf.text('Vedere De Sus', 15, y); y += 3; pdf.addImage(topImg, 'JPEG', 15, y, 180, 75); y += 79; }

    // PAGES 2+: Individual walls
    const wallViews = [
      { key: 'back', mode: 'back', label: 'Perete Spate' },
      { key: 'left', mode: 'left', label: 'Perete Stang' },
      { key: 'right', mode: 'right', label: 'Perete Drept' },
      { key: 'front', mode: 'front', label: 'Perete Fata' },
      { key: 'floor', mode: 'floor', label: 'Podea' },
    ];

    for (const wv of wallViews) {
      const surfZones = zones[wv.key] || [{ pct: 100, product: null }];
      if (!surfZones.some(z => z.product)) continue;

      pdf.addPage(); y = 15;
      pdf.setFontSize(14); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0);
      pdf.text(wv.label, 15, y); y += 8;

      const wallImg = captureView(wv.mode);
      if (wallImg) { pdf.addImage(wallImg, 'JPEG', 15, y, 180, 85); y += 90; }

      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
      pdf.text('Instructiuni Montaj:', 15, y); y += 5;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
      pdf.text(`- Tip montaj: ${getPatternLabel()}`, 18, y); y += 4;
      pdf.text(`- Punct de plecare: ${getStartLabel()}`, 18, y); y += 4;
      pdf.text(`- Culoare chit: ${GROUT_COLORS.find(g => g.value === groutColor)?.name || 'Gri'} (rost 2mm)`, 18, y); y += 4;
      pdf.text(`- Adeziv: 7.5 kg/mp`, 18, y); y += 7;

      const isFloor = wv.key === 'floor';
      const surfW = isFloor ? roomWidth : (wv.key === 'left' || wv.key === 'right' ? roomDepth : roomWidth);
      const surfH = isFloor ? roomDepth : roomHeight;

      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
      pdf.text('Materiale:', 15, y); y += 5;
      pdf.text('Lungime', 18, y); pdf.text('Produs', 48, y); pdf.text('Dim.', 105, y); pdf.text('mp', 130, y); pdf.text('Placi', 145, y); pdf.text('+10%', 162, y);
      if (pdfWithPrice) pdf.text('Pret', 180, y);
      y += 2; pdf.setDrawColor(180); pdf.line(18, y, pw - 15, y); y += 4;
      pdf.setFont('helvetica', 'normal');

      let sA = 0, sT = 0, sTM = 0, sP = 0;
      surfZones.forEach((z) => {
        if (!z.product) return;
        const dims = getTileDims(z.product, tileOrientation);
        const zArea = surfW * surfH * z.pct / 100;
        const t = Math.ceil(zArea / (dims.w * dims.h));
        const tm = Math.ceil(t * 1.1);
        const pr = z.product.price > 0 ? zArea * z.product.price : 0;
        sA += zArea; sT += t; sTM += tm; sP += pr;
        pdf.text(`${Math.round(surfW * 100 * z.pct / 100)}cm`, 18, y);
        pdf.text(fixRo(z.product.name.substring(0, 28)), 48, y);
        pdf.text(`${Math.round(dims.w*100)}x${Math.round(dims.h*100)} ${getTileFinish(z.product)}`, 105, y);
        pdf.text(`${zArea.toFixed(2)}`, 130, y); pdf.text(`${t}`, 145, y); pdf.text(`${tm}`, 162, y);
        if (pdfWithPrice) pdf.text(pr > 0 ? `${pr.toFixed(0)} RON` : '-', 180, y);
        y += 5;
      });
      y += 1; pdf.line(18, y, pw - 15, y); y += 4;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Total:', 18, y); pdf.text(`${sA.toFixed(2)}`, 130, y); pdf.text(`${sT}`, 145, y); pdf.text(`${sTM}`, 162, y);
      if (pdfWithPrice && sP > 0) pdf.text(`${sP.toFixed(0)} RON`, 180, y);
      y += 5; pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
      pdf.text(`Adeziv: ${(sA*7.5).toFixed(1)} kg (${Math.ceil(sA*7.5/25)} saci) | Chit: ${(sA*0.3).toFixed(1)} kg (${Math.ceil(sA*0.3/5)} saci)`, 18, y);
    }

    // FINAL PAGE: Grand Total
    pdf.addPage(); y = 15;
    pdf.setFontSize(16); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0);
    pdf.text('Rezumat Total', 15, y); y += 10;

    let tA = 0, tT = 0, tTM = 0, tP = 0;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
    pdf.text('Suprafata', 15, y); pdf.text('mp', 100, y); pdf.text('Placi', 118, y); pdf.text('+10%', 136, y);
    if (pdfWithPrice) pdf.text('Pret', 155, y);
    y += 2; pdf.line(15, y, pw - 15, y); y += 4; pdf.setFont('helvetica', 'normal');

    for (const sk of SURFACE_KEYS) {
      const sz = zones[sk] || [{ pct: 100, product: null }];
      const iF = sk === 'floor';
      const sW = iF ? roomWidth : (sk === 'left' || sk === 'right' ? roomDepth : roomWidth);
      const sH = iF ? roomDepth : roomHeight;
      let a = 0, t = 0, tm = 0, p = 0;
      for (const z of sz) { if (!z.product) continue; const d = getTileDims(z.product, tileOrientation); const za = sW*sH*z.pct/100; const tn = Math.ceil(za/(d.w*d.h)); a += za; t += tn; tm += Math.ceil(tn*1.1); p += z.product.price > 0 ? za*z.product.price : 0; }
      if (a === 0) continue;
      tA += a; tT += t; tTM += tm; tP += p;
      pdf.text(SURFACE_LABELS_PDF[sk], 15, y); pdf.text(`${a.toFixed(2)}`, 100, y); pdf.text(`${t}`, 118, y); pdf.text(`${tm}`, 136, y);
      if (pdfWithPrice && p > 0) pdf.text(`${p.toFixed(0)} RON`, 155, y);
      y += 5;
    }
    y += 1; pdf.setDrawColor(100); pdf.line(15, y, pw - 15, y); y += 5;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
    pdf.text('TOTAL:', 15, y); pdf.text(`${tA.toFixed(2)} mp`, 100, y); pdf.text(`${tT}`, 118, y); pdf.text(`${tTM}`, 136, y);
    if (pdfWithPrice && tP > 0) pdf.text(`${tP.toFixed(0)} RON`, 155, y);
    y += 10;

    pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
    pdf.text('Materiale Suplimentare:', 15, y); y += 6;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    pdf.text(`Adeziv (7.5 kg/mp): ${(tA*7.5).toFixed(1)} kg  (${Math.ceil(tA*7.5/25)} saci x 25kg)`, 15, y); y += 5;
    pdf.text(`Chit rosturi (0.3 kg/mp): ${(tA*0.3).toFixed(1)} kg  (${Math.ceil(tA*0.3/5)} saci x 5kg)`, 15, y); y += 5;
    pdf.text(`Culoare chit: ${GROUT_COLORS.find(g => g.value === groutColor)?.name || 'Gri'}`, 15, y); y += 8;

    pdf.setFontSize(8); pdf.setTextColor(120);
    pdf.text('* "+10%" = marja pierdere recomandata. Consumul poate varia.', 15, y); y += 4;
    pdf.text(`Generat de Shagun Surfaces - shagunsurfaces.ro | ${new Date().toLocaleDateString('ro-RO')}`, 15, y);

    pdf.save('plan-montaj-baie-shagun.pdf');
  };

  const currentProducts = products[activeTab] || [];

  return (
    <Layout>
      <SEO title="Configurator Baie 3D | Shagun Surfaces" description="Configurează-ți baia online cu gresia și obiectele sanitare din catalogul nostru." canonicalUrl="https://shagunsurfaces.ro/configurator-baie" />

      <section className="relative pt-24 pb-2 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-2 text-sm mb-2">
            <Link to="/" className="text-[#A3A3A3] hover:text-[#D4AF37]">Acasă</Link>
            <ChevronRight size={14} className="text-[#A3A3A3]" />
            <span className="text-[#D4AF37]">Configurator Baie</span>
          </nav>
          <h1 className="font-serif text-2xl sm:text-3xl text-white mb-2" data-testid="configurator-title">Configurator Baie 3D</h1>

          {/* WIZARD STEP INDICATOR */}
          <div className="flex items-center gap-1 mb-3" data-testid="wizard-steps">
            {['Dimensiuni', 'Placaj', 'Montaj', 'Obiecte', 'Finalizare'].map((label, i) => (
              <button key={i} onClick={() => setWizardStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-all ${wizardStep === i ? 'bg-[#D4AF37] text-black' : i < wizardStep ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30' : 'bg-[#1a1a1a] text-[#666] border border-white/10'}`}
                data-testid={`wizard-step-${i}`}>
                <span className={`w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full ${wizardStep === i ? 'bg-black text-[#D4AF37]' : i < wizardStep ? 'bg-[#D4AF37] text-black' : 'bg-[#333] text-[#666]'}`}>{i < wizardStep ? '✓' : i + 1}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          <div className="flex flex-col lg:flex-row gap-4">
            {/* LEFT: 3D Canvas + Step controls */}
            <div className="flex-1">
              {/* 3D Canvas - always visible */}
              <div className="relative bg-[#1a1a1a] border border-white/10 overflow-hidden" style={{ height: '460px', cursor: dragMode ? 'grab' : 'default' }} data-testid="3d-canvas">
                <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
                {wizardStep === 1 && !dragMode && (
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
                    {SURFACE_KEYS.map(k => (
                      <button key={k} onClick={() => { setSelectedSurface(k); setSelectedZoneIdx(0); }}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded transition-all ${selectedSurface === k ? 'bg-[#D4AF37] text-black' : 'bg-black/70 text-white/80 border border-white/20'}`}
                        data-testid={`surface-btn-${k}`}>{SURFACE_LABELS[k]}{zones[k]?.some(z => z.product) && ' ✓'}</button>
                    ))}
                  </div>
                )}
                {dragMode && <div className="absolute top-3 left-3 bg-[#D4AF37]/90 text-black text-xs px-3 py-1.5 rounded font-semibold z-10">Trage obiectele pentru a le muta</div>}
                {/* View toggle - always visible */}
                <div className="absolute top-3 right-3 flex gap-1 z-10">
                  <button onClick={() => setViewMode('perspective')} className={`px-2 py-1 text-[10px] rounded transition-all ${viewMode === 'perspective' ? 'bg-[#D4AF37] text-black' : 'bg-black/70 text-white/70 border border-white/20'}`} data-testid="view-perspective-btn">Laterală</button>
                  <button onClick={() => setViewMode('top')} className={`px-2 py-1 text-[10px] rounded transition-all ${viewMode === 'top' ? 'bg-[#D4AF37] text-black' : 'bg-black/70 text-white/70 border border-white/20'}`} data-testid="view-top-btn">De Sus</button>
                </div>
                {/* Bottom buttons */}
                <div className="absolute bottom-3 right-3 flex gap-2 z-10">
                  <button onClick={undoLast} disabled={zonesHistory.length === 0} className={`bg-black/70 border border-white/20 px-2.5 py-1.5 rounded text-xs flex items-center gap-1 ${zonesHistory.length > 0 ? 'text-white/80 hover:text-white' : 'text-white/30 cursor-not-allowed'}`} data-testid="undo-btn"><RotateCcw size={13} /> Înapoi</button>
                  <button onClick={resetAll} className="bg-black/70 text-white/80 border border-white/20 px-2.5 py-1.5 rounded text-xs flex items-center gap-1" data-testid="reset-btn"><RotateCcw size={13} /> Reset</button>
                </div>
              </div>

              {/* ===== STEP 0: DIMENSIUNI ===== */}
              {wizardStep === 0 && (
                <div className="mt-3 p-4 bg-[#121212] border border-white/5 rounded" data-testid="step-dimensions">
                  <h3 className="text-[#D4AF37] text-sm font-semibold mb-3 flex items-center gap-2"><Maximize2 size={15} /> Pas 1: Dimensiunile Băii</h3>
                  <p className="text-[#888] text-xs mb-4">Introdu dimensiunile camerei de baie în metri.</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <div>
                      <label className="text-[#888] text-[10px] block mb-1">Lățime (m)</label>
                      <input type="number" value={roomWidth} onChange={e => setRoomWidth(parseFloat(e.target.value) || 2)} min="1.5" max="6" step="0.1" className="w-20 bg-[#1a1a1a] border border-white/10 text-white text-sm px-2 py-1.5 rounded text-center" />
                    </div>
                    <span className="text-[#666] text-lg mt-4">×</span>
                    <div>
                      <label className="text-[#888] text-[10px] block mb-1">Adâncime (m)</label>
                      <input type="number" value={roomDepth} onChange={e => setRoomDepth(parseFloat(e.target.value) || 2)} min="1.5" max="6" step="0.1" className="w-20 bg-[#1a1a1a] border border-white/10 text-white text-sm px-2 py-1.5 rounded text-center" />
                    </div>
                    <span className="text-[#666] text-lg mt-4">×</span>
                    <div>
                      <label className="text-[#888] text-[10px] block mb-1">Înălțime (m)</label>
                      <input type="number" value={roomHeight} onChange={e => setRoomHeight(parseFloat(e.target.value) || 2.4)} min="2" max="3.5" step="0.1" className="w-20 bg-[#1a1a1a] border border-white/10 text-white text-sm px-2 py-1.5 rounded text-center" />
                    </div>
                  </div>
                </div>
              )}

              {/* ===== STEP 1: PLACAJ SUPRAFEȚE ===== */}
              {wizardStep === 1 && (
                <div className="mt-3 p-4 bg-[#121212] border border-white/5 rounded" data-testid="step-tiles">
                  <h3 className="text-[#D4AF37] text-sm font-semibold mb-3 flex items-center gap-2"><SplitSquareHorizontal size={15} /> Pas 2: Alege Gresia</h3>
                  <p className="text-[#888] text-xs mb-3">Selectează suprafața din butoanele de pe 3D, apoi alege gresia din catalog (dreapta). Poți adăuga zone pe fiecare suprafață.</p>
                  <WallZonePanel surfaceKey={selectedSurface} zones={zones} setZones={setZones} products={currentProducts} getImageUrl={getImageUrl} roomW={roomWidth} roomD={roomDepth} roomH={roomHeight} selectedZoneIdx={selectedZoneIdx} setSelectedZoneIdx={setSelectedZoneIdx} />
                </div>
              )}

              {/* ===== STEP 2: SETĂRI MONTAJ ===== */}
              {wizardStep === 2 && (
                <div className="mt-3 p-4 bg-[#121212] border border-white/5 rounded" data-testid="step-mounting">
                  <h3 className="text-[#D4AF37] text-sm font-semibold mb-4 flex items-center gap-2"><Maximize2 size={15} /> Pas 3: Setări Montaj</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Orientare Placa</label>
                      <div className="flex gap-2">
                        {[{ name: 'Orizontala', value: 'horizontal' }, { name: 'Verticala', value: 'vertical' }].map(o => (
                          <button key={o.value} onClick={() => setTileOrientation(o.value)}
                            className={`px-4 py-2 text-xs rounded transition-all ${tileOrientation === o.value ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10 hover:border-white/20'}`}
                            data-testid={`orientation-${o.value}`}>{o.name}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Tip Montaj</label>
                      <div className="flex gap-2">
                        {TILE_PATTERNS.map(p => (
                          <button key={p.value} onClick={() => setTilePattern(p.value)}
                            className={`px-4 py-2 text-xs rounded transition-all ${tilePattern === p.value ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10 hover:border-white/20'}`}
                            data-testid={`pattern-${p.value}`}>{p.name}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Punct de Plecare</label>
                      <div className="flex gap-2">
                        {START_POINTS.map(s => (
                          <button key={s.value} onClick={() => setStartPoint(s.value)}
                            className={`px-4 py-2 text-xs rounded transition-all ${startPoint === s.value ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10 hover:border-white/20'}`}
                            data-testid={`start-${s.value}`}>{s.name}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Culoare Chit</label>
                      <div className="flex gap-2">
                        {GROUT_COLORS.map(g => (
                          <button key={g.value} onClick={() => setGroutColor(g.value)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded transition-all ${groutColor === g.value ? 'ring-2 ring-[#D4AF37] bg-[#1a1a1a] text-white font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10 hover:border-white/20'}`}
                            data-testid={`grout-${g.name.toLowerCase()}`}>
                            <span className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: g.value }} />
                            {g.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== STEP 3: OBIECTE SANITARE ===== */}
              {wizardStep === 3 && (
                <div className="mt-3 p-4 bg-[#121212] border border-white/5 rounded" data-testid="step-objects">
                  <h3 className="text-[#D4AF37] text-sm font-semibold mb-4 flex items-center gap-2"><Eye size={15} /> Pas 4: Obiecte Sanitare</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Culoare WC</label>
                      <div className="flex gap-2">
                        {[{ name: 'Alb', value: '#ffffff' }, { name: 'Gri', value: '#c0c0c0' }, { name: 'Negru', value: '#333333' }].map(c => (
                          <button key={c.value} onClick={() => setWcColor(c.value)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded transition-all ${wcColor === c.value ? 'ring-2 ring-[#D4AF37] bg-[#1a1a1a] text-white font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10'}`}
                            data-testid={`wc-color-${c.name.toLowerCase()}`}>
                            <span className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: c.value }} />
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Mobilier — Dimensiune</label>
                      <div className="flex gap-2">
                        {[60, 80, 120].map(s => (
                          <button key={s} onClick={() => setFurnitureSize(s)}
                            className={`px-4 py-2 text-xs rounded transition-all ${furnitureSize === s ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10'}`}
                            data-testid={`furniture-size-${s}`}>{s} cm</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Mobilier — Culoare</label>
                      <div className="flex gap-2">
                        {[{ name: 'Lemn', value: 'lemn', color: '#8B6914' }, { name: 'Alb', value: 'alb', color: '#f5f5f5' }, { name: 'Gri', value: 'gri', color: '#888888' }, { name: 'Negru', value: 'negru', color: '#222222' }].map(c => (
                          <button key={c.value} onClick={() => setFurnitureColor(c.value)}
                            className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded transition-all ${furnitureColor === c.value ? 'ring-2 ring-[#D4AF37] bg-[#1a1a1a] text-white font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10'}`}
                            data-testid={`furniture-color-${c.value}`}>
                            <span className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: c.color }} />
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Tip Baie</label>
                      <div className="flex gap-2">
                        {[{ name: 'Dus Walk-in', value: 'shower' }, { name: 'Cada', value: 'bathtub' }].map(t => (
                          <button key={t.value} onClick={() => setBathType(t.value)}
                            className={`px-4 py-2 text-xs rounded transition-all ${bathType === t.value ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10'}`}
                            data-testid={`bath-type-${t.value}`}>{t.name}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Forma Oglinda</label>
                      <div className="flex gap-2">
                        {[{ name: 'Rotunda', value: 'round' }, { name: 'Dreptunghi', value: 'rectangular' }, { name: 'Ovala', value: 'oval' }].map(s => (
                          <button key={s.value} onClick={() => setMirrorShape(s.value)}
                            className={`px-4 py-2 text-xs rounded transition-all ${mirrorShape === s.value ? 'bg-[#D4AF37] text-black font-semibold' : 'bg-[#1a1a1a] text-[#888] border border-white/10'}`}
                            data-testid={`mirror-${s.value}`}>{s.name}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-xs font-medium mb-2 block">Muta Obiectele</label>
                      <button onClick={() => setDragMode(!dragMode)} className={`flex items-center gap-2 px-4 py-2 text-xs rounded border transition-all ${dragMode ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'bg-[#1a1a1a] text-[#888] border-white/10 hover:border-white/20'}`} data-testid="drag-mode-btn">
                        <Move size={14} /> {dragMode ? 'Mutare Activa — trage in 3D' : 'Activeaza Mutarea'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== STEP 4: FINALIZARE ===== */}
              {wizardStep === 4 && (
                <div className="mt-3 p-4 bg-[#121212] border border-white/5 rounded" data-testid="step-finalize">
                  <h3 className="text-[#D4AF37] text-sm font-semibold mb-4 flex items-center gap-2"><FileText size={15} /> Pas 5: Finalizare</h3>
                  <p className="text-[#888] text-xs mb-4">Verifică configurația în 3D. Descarcă PDF-ul pentru montator sau salvează imaginea.</p>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={exportPDF} className="flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded text-sm font-semibold hover:bg-gray-100 transition-all" data-testid="pdf-btn">
                      <FileText size={16} /> Descarcă PDF {pdfWithPrice ? 'Cu Preț' : 'Fără Preț'}
                    </button>
                    <button onClick={() => setPdfWithPrice(!pdfWithPrice)} className={`px-4 py-2.5 rounded text-sm border transition-all ${pdfWithPrice ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40 font-semibold' : 'bg-[#1a1a1a] text-[#888] border-white/10'}`} data-testid="price-toggle">
                      {pdfWithPrice ? 'Prețuri: DA ✓' : 'Prețuri: NU'}
                    </button>
                    <button onClick={takeScreenshot} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black rounded text-sm font-semibold hover:bg-[#c4a030] transition-all" data-testid="download-btn">
                      <Download size={16} /> Descarcă Imagine
                    </button>
                  </div>
                </div>
              )}

              {/* WIZARD NAVIGATION */}
              <div className="mt-3 flex items-center justify-between">
                <button onClick={() => setWizardStep(Math.max(0, wizardStep - 1))} disabled={wizardStep === 0}
                  className={`px-5 py-2 text-sm rounded border transition-all ${wizardStep > 0 ? 'bg-[#1a1a1a] text-white border-white/20 hover:border-white/40' : 'bg-[#0e0e0e] text-[#444] border-white/5 cursor-not-allowed'}`}
                  data-testid="wizard-prev">← Pasul Anterior</button>
                <span className="text-[#555] text-xs">Pas {wizardStep + 1} din 5</span>
                <button onClick={() => setWizardStep(Math.min(4, wizardStep + 1))} disabled={wizardStep === 4}
                  className={`px-5 py-2 text-sm rounded border transition-all ${wizardStep < 4 ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-semibold hover:bg-[#c4a030]' : 'bg-[#0e0e0e] text-[#444] border-white/5 cursor-not-allowed'}`}
                  data-testid="wizard-next">Pasul Următor →</button>
              </div>
            </div>

            {/* RIGHT: Catalog sidebar - only in Step 1 (Placaj) */}
            {wizardStep === 1 && (
              <div className="lg:w-80 xl:w-96">
                <div className="sticky top-24">
                  <div className="flex flex-wrap gap-1 mb-2" data-testid="catalog-tabs">
                    {CATALOG_TABS.map(tab => (
                      <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        className={`px-2.5 py-1 text-[10px] rounded font-medium transition-all ${activeTab === tab.key ? 'bg-[#D4AF37] text-black' : 'bg-[#1a1a1a] text-white/70 border border-white/10'}`}
                        data-testid={`tab-${tab.key}`}>{tab.label}</button>
                    ))}
                  </div>
                  <div className="max-h-[540px] overflow-y-auto pr-1 space-y-1.5" data-testid="tile-catalog">
                    {loading ? (
                      <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-[#1a1a1a] animate-pulse rounded" />)}</div>
                    ) : currentProducts.length === 0 ? (
                      <div className="text-center py-6"><p className="text-[#A3A3A3] text-sm">Nu există produse.</p><p className="text-[#555] text-xs">Adaugă din Admin.</p></div>
                    ) : (
                      currentProducts.map(product => {
                        const imgUrl = getImageUrl(product);
                        const dims = getTileDims(product, tileOrientation);
                        return (
                          <button key={product.id} onClick={() => handleProductClick(product)}
                            className="w-full flex items-center gap-2.5 p-1.5 rounded border border-white/5 bg-[#121212] hover:border-white/20 hover:bg-[#1a1a1a] transition-all text-left"
                            data-testid={`tile-option-${product.id}`}>
                            {imgUrl ? <img src={imgUrl} alt={product.name} className="w-12 h-12 object-cover rounded flex-shrink-0" loading="lazy" /> : <div className="w-12 h-12 bg-[#333] rounded flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-xs font-medium">{product.name}</p>
                              <p className="text-[#666] text-[10px]">{product.code} · {Math.round(dims.w * 100)}×{Math.round(dims.h * 100)}cm · <span className={getTileFinish(product) === 'lucios' ? 'text-blue-300' : getTileFinish(product) === 'satinat' ? 'text-purple-300' : 'text-[#888]'}>{getTileFinish(product) === 'lucios' ? 'Lucios' : getTileFinish(product) === 'satinat' ? 'Satinat' : 'Mat'}</span></p>
                              {product.price > 0 && <p className="text-[#D4AF37] text-[10px] font-semibold">{product.price} RON/mp</p>}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BathroomConfiguratorPage;
